import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { ErrorBoundary } from 'react-error-boundary';
import WebMercatorViewport from 'viewport-mercator-project';
import { uniqBy } from 'lodash';
import { GeoJsonPoint } from '@maiia/model/generated/model/api-patient/api-patient';
import { useRouter } from 'next/router';
import SearchContext from '../../templates/Search/SearchContext';

import config from '../../../src/config';
import MapFallback from '../MapFallback/MapFallback';
import MapServerSettingsDetector from '@/components/templates/Main/MapServerSettingsDetector';

const TYPE_OPENSTREETMAP = 'openstreetmap';

const MapAdapter = dynamic(
  () =>
    config.get('MAP_TYPE', TYPE_OPENSTREETMAP) === TYPE_OPENSTREETMAP
      ? import('./OpenStreetMap')
      : import('./Mapbox'),
  { ssr: false },
);

type Props = {
  items: Array<any>;
  onItemSelected?: Function;
  useSearchContext?: boolean;
  initialLocation?: Partial<GeoJsonPoint>;
};

const getViewport = (wrapperEl, viewport, items) => {
  if (items.length === 0) {
    return;
  }

  if (!wrapperEl.current) {
    return;
  }

  const uniqueItemsByAddresses: any[] = uniqBy(
    items,
    ({
      publicInformation: {
        address: {
          location: { x, y },
        },
      },
    }) => `${x}_${y}`,
  );

  if (uniqueItemsByAddresses.length === 1) {
    return {
      longitude: uniqueItemsByAddresses[0].publicInformation.address.location.x,
      latitude: uniqueItemsByAddresses[0].publicInformation.address.location.y,
    };
  }

  const longitudes = uniqueItemsByAddresses.map(
    item => item.publicInformation.address.location.x,
  );
  const latitudes = uniqueItemsByAddresses.map(
    item => item.publicInformation.address.location.y,
  );

  const width = wrapperEl.current.offsetWidth;
  const height = wrapperEl.current.offsetHeight;
  const fitViewport = new WebMercatorViewport({
    ...viewport,
    width,
    height,
  });
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);
  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);

  const { longitude, latitude, zoom } = fitViewport.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ], // { padding: 50 },
  );

  return { longitude, latitude, zoom };
};

const initialViewport = {
  zoom: 15,
  bearing: 0,
  pitch: 0,
};

const MapComponent = ({
  items: dirtyItems,
  onItemSelected,
  useSearchContext,
  initialLocation,
  ...props
}: Props) => {
  /**
   * The backend sometimes returns dirty items that doesn't have x/y informations.
   * Until they fix it, we filter out the buggy items
   */
  const items = dirtyItems.filter(
    item =>
      Boolean(item?.publicInformation?.address?.location?.x) &&
      Boolean(item?.publicInformation?.address?.location?.y),
  );
  const wrapperEl = React.useRef<HTMLDivElement>(null);
  // itemsRef for keep previous items when it changed
  const itemsRef = React.useRef<any>(null!);
  // viewport from parent
  const ctx = React.useContext(SearchContext);
  const [selfViewport, setSelfViewport] = useState({
    ...initialViewport,
  });
  const goToMarker = ref => {
    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const onViewportChange = React.useCallback(
    newViewport => {
      // console.log(useSearchContext, ctx.mapViewport, newViewport)
      if (useSearchContext) {
        ctx.setMapViewport(newViewport);
      } else {
        setSelfViewport(newViewport);
      }
    },
    [useSearchContext],
  );

  useEffect(() => {
    if (useSearchContext) {
      const viewport = {
        ...initialViewport,
        ...getViewport(wrapperEl, initialViewport, items),
      };
      ctx.setMapViewport(viewport);
      ctx.setMapViewportDefault(viewport);
    } else if (initialLocation?.x && initialLocation?.y) {
      setSelfViewport({
        ...selfViewport,
        // @ts-ignore
        longitude: initialLocation.x,
        latitude: initialLocation.y,
      });
    } else {
      setSelfViewport({
        ...selfViewport,
        ...getViewport(wrapperEl, selfViewport, items),
      });
    }
  }, []);

  // Update viewport when items changed, only apply for case selfViewport handle
  // Use in ProfileContent
  useEffect(() => {
    // First time rendering, set itemsRef.current to items
    if (!itemsRef.current) {
      itemsRef.current = items;
      return;
    }
    // Check if any items changed
    if (items.some((item, index) => itemsRef.current[index].id !== item.id)) {
      setSelfViewport({
        ...selfViewport,
        ...getViewport(wrapperEl, selfViewport, items),
      });
      itemsRef.current = items;
    }
  }, [items]);
  return (
    <div id="map" ref={wrapperEl}>
      <MapServerSettingsDetector />
      {React.createElement(MapAdapter, {
        viewport: useSearchContext ? ctx.mapViewport : selfViewport,
        items,
        useSearchContext,
        goToMarker,
        onViewportChange,
        selfViewport,
        onItemSelected,
        ...ctx,
        ...props,
      })}
    </div>
  );
};

const Map = props => {
  // Use key to re-init component on route change, useful on search pages
  // We may update the MapComponent to optimize it and avoid a complete render
  const router = useRouter();
  const key = router.asPath;
  return (
    <ErrorBoundary key={key} FallbackComponent={MapFallback}>
      <MapComponent {...props} />
    </ErrorBoundary>
  );
};

export default Map;
